import { FC } from 'react';
import { Button } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { PropsWithClassName } from '@/types/PropsWithClassName';
import { cn } from '@/utils/classNames.ts';
import { Link } from 'react-router-dom';
import type { ProductType as ProductTypeT } from '$/_const/ProductType.ts';
import ProductType from '$/_const/ProductType.ts';
import { useAuthContext } from '@/context/AuthContext.tsx';

type TrialBlockProps = PropsWithClassName<{
  subscriptionType: Omit<ProductTypeT, 'credit_package'>;
  daysRemaining: number;
  row?: boolean;
}>;

const TrialBlock: FC<TrialBlockProps> = ({ daysRemaining, className, row = false, subscriptionType }) => {
  const { t } = useTranslation();
  const { getSubscriptionPage } = useAuthContext();

  const isTrial = subscriptionType === ProductType.TRIAL;
  const hasEnded = daysRemaining === 0;

  return !isTrial && !hasEnded ? null : (
    <div
      className={cn(
        'rounded-xl border border-primary bg-primary bg-opacity-15 py-4 px-6 flex gap-3',
        {
          'flex-wrap justify-between items-center': row,
          'flex-col': !row,
        },
        className
      )}
    >
      <div className="flex flex-col">
        <p className="text-primary text-xs font-bold uppercase">
          {isTrial && !hasEnded ? t('trialBlockComponent.trial_active') : null}
          {!isTrial && !hasEnded ? t('trialBlockComponent.subscription_active') : null}
          {hasEnded ? t('trialBlockComponent.subscription') : null}
        </p>
        <p
          className={cn('font-feature font-bold italic text-2xl', {
            'text-xl': row,
            'text-2xl': !row,
          })}
        >
          {!hasEnded ? t('tripNavbar.days_remaining', { count: daysRemaining }) : null}
          {isTrial && hasEnded ? t('trialBlockComponent.trial_ended') : null}
          {!isTrial && hasEnded ? t('trialBlockComponent.subscription_ended') : null}
        </p>
      </div>
      {hasEnded || isTrial ? (
        <div>
          <Link to={getSubscriptionPage()}>
            <Button
              color="ghost"
              size="sm"
              variant="link"
              className={cn('text-primary font-bold no-underline px-0', {
                'text-sm': row,
                'text-base': !row,
              })}
              endIcon={<ArrowRightIcon className="w-4 h-4" />}
            >
              {t('button.subscribe')}
            </Button>
          </Link>
        </div>
      ) : null}
    </div>
  );
};

export default TrialBlock;
